import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import {
  createConsumableItem,
  fetchConsumableItems,
  fetchConsumablesCentreSettings,
  fetchMyCentres,
  setConsumablesOwnRoster,
  updateConsumableItem
} from 'api/procurement'
import { confirmDialog, notify } from 'app/helpers'
import { orderCentres } from '../constants'

// The shared consumables catalogue (Azam 2026-07-21): HQ maintains the GLOBAL
// items (centre = null) every centre picks from, and edits their price. Centres
// no longer add ad-hoc items, so this is the single source of the roster.
//
// A centre can also run a roster of its own (2026-08-05): the scope picker
// switches this page between the shared list and one centre's items, and a
// centre marked `own_roster_only` stops seeing the shared list altogether. The
// Kolkata centres are the reason — their list diverged from the shared one to
// the point where the two overlap on barely any item.
const SHARED = 'shared'
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(1) },
  scopeBar: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5)
  },
  addBar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    border: '1px solid #e9e6e0',
    borderRadius: 8,
    background: '#faf8f5'
  },
  priceInput: { width: 110, '& input': { fontSize: 13, padding: '5px 6px' } },
  stickyHead: {
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: theme.palette.grey[100]
    }
  }
}))

const inr = (n) =>
  n == null || n === '' ? '—' : `₹${Number(n).toLocaleString('en-IN')}`

// `readOnly` (centre team, 2026-07-21): centres only VIEW the roster + prices —
// no add, no price edits.
const HqConsumablesCatalogue = ({ readOnly = false, centreId = '' }) => {
  const classes = useStyles()
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [form, setForm] = React.useState({ name: '', unit: '', default_price: '' })
  const [priceEdits, setPriceEdits] = React.useState({})
  // Which list is on screen: the shared catalogue, or one centre's own items.
  const [scope, setScope] = React.useState(SHARED)
  const [centres, setCentres] = React.useState([])
  const [ownRoster, setOwnRoster] = React.useState(null)

  // The settings load in both modes: HQ needs them for the picker, and a centre
  // needs them to know whether the shared list is even part of its roster.
  React.useEffect(() => {
    if (!readOnly) {
      fetchMyCentres()
        .then((d) => setCentres(orderCentres(d?.results || [])))
        .catch(() => setCentres([]))
    }
    fetchConsumablesCentreSettings()
      .then((d) => {
        const map = {}
        ;(d?.results || []).forEach((r) => {
          map[r.centre] = !!r.own_roster_only
        })
        setOwnRoster(map)
      })
      .catch(() => setOwnRoster({}))
  }, [readOnly])

  // HQ edits one scope at a time. A centre views its own effective roster —
  // its items, plus the shared list unless it has been taken off it.
  const centreScope = readOnly ? centreId : scope === SHARED ? '' : scope
  const settled = ownRoster !== null
  const rosterFlags = ownRoster || {}
  const isOwnRoster = !!rosterFlags[centreScope]

  // What the centre would actually see on its order form.
  const liveCount = items.filter((i) => i.is_active !== false).length

  const load = React.useCallback(() => {
    // Wait for the settings: without them a centre on its own roster would
    // flash the shared list it is not supposed to see.
    if (!settled) return
    setLoading(true)
    const rows = (d) => (Array.isArray(d) ? d : d?.results || [])
    // A centre sees only what it can actually order; HQ sees the retired rows
    // too, so an item switched off the list can be switched back on.
    const live = readOnly ? { is_active: true } : {}
    const queries = []
    if (centreScope) queries.push({ ...live, centre: centreScope })
    if (!centreScope || (readOnly && !isOwnRoster)) {
      queries.push({ ...live, is_global: true })
    }
    Promise.all(queries.map((q) => fetchConsumableItems(q)))
      .then((res) => {
        const list = res.flatMap(rows)
        list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
        setItems(list)
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [centreScope, isOwnRoster, readOnly, settled])
  React.useEffect(load, [load])

  const add = async () => {
    if (!form.name.trim()) return
    setBusy(true)
    try {
      await createConsumableItem({
        name: form.name.trim(),
        unit: form.unit.trim(),
        default_price: Number(form.default_price) || 0,
        // Blank centre = a shared item every centre sees.
        ...(centreScope ? { centre: centreScope } : {})
      })
      setForm({ name: '', unit: '', default_price: '' })
      notify('Item added to the catalogue', '', 'success')
      load()
    } catch (e) {
      notify('Could not add the item', '', 'error')
    } finally {
      setBusy(false)
    }
  }

  // Moving a centre onto its own roster hides the shared list from it, which is
  // a big enough change to the screen its team fills every month to confirm.
  const toggleOwnRoster = async (next) => {
    const centre = centres.find((c) => c.uuid === centreScope)
    const name = centre?.name || 'this centre'
    const { isConfirmed } = await confirmDialog({
      text: next
        ? `${name} will stop seeing the shared catalogue and fill only from its own ` +
          `${items.length} item${items.length === 1 ? '' : 's'}.`
        : `${name} will go back to the shared catalogue, on top of its own items.`,
      confirmButtonText: next ? 'Use its own roster' : 'Use the shared catalogue',
      options: { title: 'Change this centre’s roster?' }
    })
    if (!isConfirmed) return
    setBusy(true)
    try {
      await setConsumablesOwnRoster(centreScope, next)
      setOwnRoster((m) => ({ ...m, [centreScope]: next }))
      notify('Roster updated', '', 'success')
    } catch (e) {
      notify('Could not update the roster', '', 'error')
    } finally {
      setBusy(false)
    }
  }

  // Off the list, off the centre's order form. Deactivating never deletes: the
  // row stays here so it can come back, and any past order still resolves it.
  const toggleActive = async (item, next) => {
    setBusy(true)
    try {
      await updateConsumableItem(item.uuid, { is_active: next })
      setItems((list) =>
        list.map((i) => (i.uuid === item.uuid ? { ...i, is_active: next } : i))
      )
      notify(next ? 'Back on the list' : 'Taken off the list', '', 'success')
    } catch (e) {
      notify('Could not update the item', '', 'error')
    } finally {
      setBusy(false)
    }
  }

  const savePrice = async (item) => {
    const v = priceEdits[item.uuid]
    if (v === undefined || v === '') return
    setBusy(true)
    try {
      await updateConsumableItem(item.uuid, { default_price: Number(v) || 0 })
      setPriceEdits((p) => {
        const n = { ...p }
        delete n[item.uuid]
        return n
      })
      notify('Price updated', '', 'success')
      load()
    } catch (e) {
      notify('Could not update the price', '', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box className={classes.root}>
      {!readOnly && (
      <div className={classes.scopeBar}>
        <TextField
          select
          size="small"
          variant="outlined"
          label="Catalogue"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          style={{ minWidth: 260 }}>
          <MenuItem value={SHARED}>Shared catalogue (all centres)</MenuItem>
          {centres.map((c) => (
            <MenuItem key={c.uuid} value={c.uuid}>
              {c.name}
              {rosterFlags[c.uuid] ? ' — own roster' : ''}
            </MenuItem>
          ))}
        </TextField>
        {centreScope ? (
          <>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  color="primary"
                  checked={isOwnRoster}
                  disabled={busy}
                  onChange={(e) => toggleOwnRoster(e.target.checked)}
                />
              }
              label={
                <Typography variant="caption">Hide the shared catalogue</Typography>
              }
            />
            <Typography variant="caption" style={{ color: '#777' }}>
              {isOwnRoster
                ? 'This centre fills from these items only.'
                : 'This centre sees these items on top of the shared catalogue.'}
            </Typography>
          </>
        ) : (
          <Typography variant="caption" style={{ color: '#777' }}>
            Every centre fills from this list, unless it is on a roster of its own.
          </Typography>
        )}
        <Chip size="small" label={`${liveCount} items`} />
        {items.length > liveCount && (
          <Chip
            size="small"
            variant="outlined"
            label={`${items.length - liveCount} off the list`}
          />
        )}
      </div>
      )}

      {readOnly && isOwnRoster && (
        <Typography
          variant="caption"
          style={{ display: 'block', color: '#777', marginBottom: 8 }}>
          This centre runs its own consumables list — the shared catalogue does not
          apply here.
        </Typography>
      )}

      {!readOnly && (
      <div className={classes.addBar}>
        <Typography variant="caption" style={{ fontWeight: 600, color: '#555' }}>
          {centreScope ? 'Add item for this centre' : 'Add item'}
        </Typography>
        <TextField
          size="small"
          variant="outlined"
          label="Name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          style={{ minWidth: 220 }}
        />
        <TextField
          size="small"
          variant="outlined"
          label="Unit"
          value={form.unit}
          onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
          style={{ width: 110 }}
        />
        <TextField
          size="small"
          variant="outlined"
          label="Price (₹)"
          type="number"
          value={form.default_price}
          onChange={(e) => setForm((f) => ({ ...f, default_price: e.target.value }))}
          style={{ width: 120 }}
        />
        <Button
          size="small"
          color="primary"
          variant="contained"
          disableElevation
          disabled={busy || !form.name.trim()}
          onClick={add}>
          Add
        </Button>
      </div>
      )}

      {loading && <LinearProgress />}
      <TableContainer component={Paper} variant="outlined" style={{ overflow: 'visible' }}>
        <Table size="small" className={classes.stickyHead}>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Price (₹)</TableCell>
              {!readOnly && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.uuid} hover>
                <TableCell
                  style={
                    item.is_active === false
                      ? { color: '#9e9e9e', textDecoration: 'line-through' }
                      : undefined
                  }>
                  {item.name}
                </TableCell>
                <TableCell>{item.unit || '—'}</TableCell>
                <TableCell>
                  {readOnly ? (
                    inr(item.default_price)
                  ) : (
                    <TextField
                      className={classes.priceInput}
                      size="small"
                      type="number"
                      value={
                        priceEdits[item.uuid] !== undefined
                          ? priceEdits[item.uuid]
                          : item.default_price
                      }
                      onChange={(e) =>
                        setPriceEdits((p) => ({ ...p, [item.uuid]: e.target.value }))
                      }
                    />
                  )}
                </TableCell>
                {!readOnly && (
                  <TableCell align="right">
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          color="primary"
                          checked={item.is_active !== false}
                          disabled={busy}
                          onChange={(e) => toggleActive(item, e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          {item.is_active === false ? 'Off the list' : 'On the list'}
                        </Typography>
                      }
                    />
                    <Button
                      size="small"
                      disabled={busy || priceEdits[item.uuid] === undefined}
                      onClick={() => savePrice(item)}>
                      Save price
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!items.length && !loading && (
              <TableRow>
                <TableCell colSpan={readOnly ? 3 : 4}>
                  <Typography variant="body2" color="textSecondary">
                    {readOnly
                      ? 'No catalogue items yet.'
                      : 'No catalogue items yet — add one above.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default HqConsumablesCatalogue
